import { type AreaChartFactory, AreaChart as MantineAreaChart } from "@mantine/charts";
import { useMantineTheme } from "@mantine/core";
import { useMemo } from "react";
import { componentWrapper } from "../../utilities";
import {
    AREA_CHART_DOT_RADIUS,
    AREA_CHART_REFERENCE_LINE_FONT_SIZE,
    AREA_CHART_REFERENCE_LINE_FONT_WEIGHT,
    AREA_CHART_REFERENCE_LINE_LABEL_POSITION,
    AREA_CHART_REFERENCE_LINE_STROKE_DASHARRAY,
    AREA_CHART_REFERENCE_LINE_STROKE_WIDTH,
    AREA_CHART_STROKE_WIDTH,
} from "./area-chart.constants";
import styles from "./area-chart.module.scss";
import type { IProps } from "./area-chart.types";

const AreaChart = componentWrapper<AreaChartFactory, IProps>("AreaChart", (props, ref) => {
    const { type, withGradient, mouseEvents, areaChartProps, ...otherProps } = props;
    const theme = useMantineTheme();

    // TODO: look into why referenceLines color, strokeWidth, and strokeDasharray are not being applied
    const referenceLinesWithDefaults = useMemo(() => {
        if (!props.referenceLines || props.referenceLines.length === 0) {
            return undefined;
        }

        return props.referenceLines.map((line) => ({
            ...line,
            color: line.color || theme.colors.gray[9],
            strokeWidth: line.strokeWidth || AREA_CHART_REFERENCE_LINE_STROKE_WIDTH,
            strokeDasharray: line.strokeDasharray || AREA_CHART_REFERENCE_LINE_STROKE_DASHARRAY,
            labelPosition: line.labelPosition || AREA_CHART_REFERENCE_LINE_LABEL_POSITION,
            fontWeight: line.fontWeight || AREA_CHART_REFERENCE_LINE_FONT_WEIGHT,
            fontSize: line.fontSize || AREA_CHART_REFERENCE_LINE_FONT_SIZE,
        }));
    }, [props.referenceLines, theme]);

    const combinedAreaChartProps = useMemo(
        () => ({
            ...areaChartProps,
            ...mouseEvents,
        }),
        [areaChartProps, mouseEvents]
    );

    return (
        <MantineAreaChart
            ref={ref}
            classNames={{ root: styles["container"], referenceLine: styles["reference-line"], axis: styles["axis"] }}
            data-type={type || "default"}
            type={type || "default"}
            {...otherProps}
            withGradient={withGradient || true}
            referenceLines={referenceLinesWithDefaults}
            dotProps={{ r: 0, ...props.dotProps }}
            activeDotProps={{ r: AREA_CHART_DOT_RADIUS, strokeWidth: 3, ...props.activeDotProps }}
            strokeWidth={props.strokeWidth || AREA_CHART_STROKE_WIDTH}
            areaChartProps={combinedAreaChartProps}
        />
    );
});

export { AreaChart };

// TODO:
// - Revisit this later: Colours for series to always appear in the specific order
